/**
 * Centralized utilities for building ECharts axis and grid configurations
 * This provides a single source of truth for axis/grid settings across all chart types
 */

import type { ChartSettings } from "../constants";
import {
  createAxisLabelFormatter,
  getGranularityAxisConfig,
  isTimeBasedData,
  type GranularityPreset,
} from "./axisGranularity";

/**
 * Build standardized X-axis configuration for ECharts
 */
export function buildXAxisConfig(
  settings: Partial<ChartSettings> | undefined,
  chartType: string,
  data?: {
    type?: "category" | "value" | "time";
    categories?: string[];
    dates?: string[];
    min?: number;
    max?: number;
    boundaryGap?: boolean;
  }
): any {
  const axisType = data?.type || "category";
  
  const config: any = {
    type: axisType,
    axisTick: { show: false },
    axisLine: {
      show: settings?.showXAxisLine === true,
      onZero: false,
      lineStyle: { color: "#94A3B8" },
    },
    splitLine: {
      show: settings?.showGrid === true,
      lineStyle: {
        type: "solid",
        color: settings?.gridColor || "#e2e8f0",
        opacity: 0.8,
      },
    },
    axisLabel: {
      fontSize: settings?.axisLabelFontSize ?? 10,
      color: settings?.axisLabelColor || "#000000",
      hideOverlap: true,
    },
  };

  // Apply data-specific configurations
  if (axisType === "category" && data?.categories) {
    config.data = data.categories;
    config.axisLabel.interval = 0;
  }

  if (data?.boundaryGap !== undefined) {
    config.boundaryGap = data.boundaryGap;
  }

  // Apply min/max if provided
  if (data?.min !== undefined) config.min = data.min;
  if (data?.max !== undefined) config.max = data.max;

  // Apply X-axis title if enabled
  if (settings?.showXAxisTitle && settings?.xAxisTitle) {
    config.name = settings.xAxisTitle;
    config.nameLocation = mapTitlePosition(settings.xAxisTitlePosition) || "middle";
    config.nameGap = 28;
    config.nameTextStyle = {
      fontSize: settings.axisLabelFontSize ?? 12,
      color: settings.axisLabelColor || "#64748b",
    };
  }

  // Apply label offset if specified
  if (settings?.xAxisLabelOffset) {
    config.axisLabel.offset = [
      settings.xAxisLabelOffset.x || 0,
      settings.xAxisLabelOffset.y || 0,
    ];
  }

  // Apply granularity settings for time-based data
  if (
    settings?.xAxisGranularity &&
    settings.xAxisGranularity !== "auto" &&
    data?.dates &&
    isTimeBasedData(data.dates)
  ) {
    const granularityConfig = getGranularityAxisConfig(
      data.dates,
      settings.xAxisGranularity as GranularityPreset
    );
    Object.assign(config.axisLabel, granularityConfig.axisLabel);
    if (granularityConfig.interval !== undefined) {
      config.axisLabel.interval = granularityConfig.interval;
    }
  } else if (settings?.xAxisGranularity) {
    // Apply custom formatter for non-time-based data
    const formatter = createAxisLabelFormatter(
      settings.xAxisGranularity as GranularityPreset
    );
    config.axisLabel.formatter = formatter;
  }

  // Apply custom tick interval if specified
  if (settings?.xAxisTickInterval !== undefined) {
    config.interval = settings.xAxisTickInterval;
  }

  // Apply min/max ticks constraints
  if (settings?.xAxisMinTicks || settings?.xAxisMaxTicks) {
    config.splitNumber = settings.xAxisMaxTicks || 5;
  }

  return config;
}

/**
 * Build standardized Y-axis configuration for ECharts
 */
export function buildYAxisConfig(
  settings: Partial<ChartSettings> | undefined,
  chartType: string,
  data?: {
    type?: "value" | "category" | "log";
    min?: number;
    max?: number;
    formatter?: (value: number) => string;
    hasNegativeValues?: boolean;
  }
): any {
  const axisType = data?.type || "value";
  
  const config: any = {
    type: axisType,
    axisTick: { show: false },
    axisLine: {
      show: settings?.showYAxisLine === true,
      onZero: data?.hasNegativeValues === true,
      lineStyle: { color: "#94A3B8" },
    },
    splitLine: {
      show: settings?.showGrid === true,
      lineStyle: {
        type: "solid",
        color: settings?.gridColor || "#e2e8f0",
        opacity: 0.8,
      },
    },
    axisLabel: {
      margin: 4,
      fontSize: settings?.axisLabelFontSize ?? 10,
      color: settings?.axisLabelColor || "#000000",
      showMinLabel: true,
      showMaxLabel: true,
    },
  };

  // Apply custom formatter if provided
  if (data?.formatter) {
    config.axisLabel.formatter = data.formatter;
  }

  // Apply min/max if provided
  if (data?.min !== undefined) config.min = data.min;
  if (data?.max !== undefined) config.max = data.max;

  // Apply Y-axis title if enabled
  if (settings?.showYAxisTitle && settings?.yAxisTitle) {
    config.name = settings.yAxisTitle;
    config.nameLocation = mapTitlePosition(settings.yAxisTitlePosition) || "middle";
    config.nameGap = 36;
    config.nameTextStyle = {
      fontSize: settings.axisLabelFontSize ?? 12,
      color: settings.axisLabelColor || "#64748b",
    };
    config.nameRotate = 90;
  }

  // Apply label offset if specified
  if (settings?.yAxisLabelOffset) {
    config.axisLabel.offset = [
      settings.yAxisLabelOffset.x || 0,
      settings.yAxisLabelOffset.y || 0,
    ];
  }

  // Apply logarithmic scale if specified
  if (settings?.yAxisGranularity === "log") {
    config.type = "log";
    config.logBase = 10;
  }

  // Apply custom tick interval if specified
  if (settings?.yAxisTickInterval !== undefined) {
    config.interval = settings.yAxisTickInterval;
  }

  // Apply min/max ticks constraints
  if (settings?.yAxisMinTicks || settings?.yAxisMaxTicks) {
    config.splitNumber = settings.yAxisMaxTicks || 5;
  }

  // Apply auto-scaling
  if (settings?.yAxisAutoScale !== false) {
    config.scale = true;
  }

  return config;
}

/**
 * Build standardized grid configuration for ECharts
 */
export function buildGridConfig(
  settings: Partial<ChartSettings> | undefined,
  context?: {
    legendVisible?: boolean;
    hasXAxisTitle?: boolean;
    hasYAxisTitle?: boolean;
    chartType?: string;
  }
): any {
  // Start with user-specified grid settings if provided
  const userGrid = settings?.grid || {};
  
  // Calculate dynamic spacing based on context
  const legendVisible = context?.legendVisible !== false;
  const hasXAxisTitle = context?.hasXAxisTitle === true;
  const hasYAxisTitle = context?.hasYAxisTitle === true;

  // Calculate bottom spacing
  let bottomSpacing = legendVisible ? 60 : 20;
  if (hasXAxisTitle) {
    bottomSpacing += 24;
  }

  // Calculate left spacing
  let leftSpacing = 30;
  if (hasYAxisTitle) {
    leftSpacing = 54; // Space for rotated Y-axis title
  }

  // Build grid config with smart defaults
  const grid: any = {
    top: userGrid.top !== undefined ? userGrid.top : 20,
    bottom: userGrid.bottom !== undefined ? userGrid.bottom : bottomSpacing,
    left: userGrid.left !== undefined ? userGrid.left : leftSpacing,
    right: userGrid.right !== undefined ? userGrid.right : 20,
    containLabel: false,
  };

  // Apply show property if grid is enabled
  if (settings?.showGrid === true) {
    grid.show = true;
    grid.borderColor = settings.gridColor || "#e2e8f0";
  }

  return grid;
}

/**
 * Map title position setting to ECharts nameLocation value
 */
function mapTitlePosition(
  position: "start" | "middle" | "end" | undefined
): "start" | "middle" | "end" | undefined {
  // Direct mapping - ECharts uses the same values
  return position;
}

/**
 * Apply axis line styles to an existing axis configuration
 */
export function applyAxisLineStyles(
  axis: any,
  settings: Partial<ChartSettings> | undefined,
  axisType: "x" | "y"
): void {
  if (!axis) return;

  const showLine =
    axisType === "x"
      ? settings?.showXAxisLine === true
      : settings?.showYAxisLine === true;

  axis.axisLine = {
    ...axis.axisLine,
    show: showLine,
    lineStyle: {
      ...axis.axisLine?.lineStyle,
      color: "#94A3B8",
    },
  };
}

/**
 * Apply axis label styles to an existing axis configuration
 */
export function applyAxisLabelStyles(
  axis: any,
  settings: Partial<ChartSettings> | undefined
): void {
  if (!axis || !axis.axisLabel) return;

  axis.axisLabel = {
    ...axis.axisLabel,
    fontSize: settings?.axisLabelFontSize ?? axis.axisLabel.fontSize ?? 10,
    color: settings?.axisLabelColor || axis.axisLabel.color || "#000000",
  };
}

